import React from 'react';
import ItemsTable from '../../components/ItemsTable';
import { toast } from '../../components/AppToast';
import { SerialCaptureButton } from '../../components/SerialCaptureModal';
import { countCapturedSerials, resolveSerialColumns } from '../../utils/serialCapture';

// Column configurations - Added PO and ASN price columns
const COLUMN_CONFIGS = {
  poNumber: { label: 'PO #', maxWidth: 180 },
  city: { label: 'City', maxWidth: 200 },
  state: { label: 'State', maxWidth: 200 },
  SKUID: { label: 'SKU ID', maxWidth: 150 },
  description: { label: 'Description', minWidth: 150, maxWidth: 600 },
  uom: { label: 'UOM', maxWidth: 100 },
  packKey: { label: 'Pack Key', maxWidth: 130 },
  lpnNo: { label: 'LPN No', maxWidth: 140 },
  batchNum: { label: 'Batch Num', maxWidth: 140 },
  manufacturingDate: { label: 'Manufacturing Date', maxWidth: 140 },
  expirationDate: { label: 'Expiration Date', maxWidth: 140 },
  itemWeight: { label: 'Weight', maxWidth: 120 },
  itemCube: { label: 'Volume', maxWidth: 120 },
  expectedQty: { label: 'Ordered Qty', maxWidth: 130 },
  itemPOTolerance: { label: 'Tolerance %', maxWidth: 120 },
  // openQty: { label: 'Open Qty', maxWidth: 150 },
  openQty: { label: 'Open Qty', maxWidth: 120 },
  shortQty: { label: 'Short Qty', maxWidth: 120 },
  excessQty: { label: 'Excess Qty', maxWidth: 120 },
  damagedQty: { label: 'Damage Qty', maxWidth: 120 },
  dispatchQty: { label: 'Dispatch Qty', maxWidth: 130 },
  serialCapture: { label: 'Serial / IMEI', maxWidth: 130 },
  receivedQty: { label: 'Received Qty', maxWidth: 130 },
  asnUnitPrice: { label: 'ASN Unit Price', maxWidth: 130 },
  asnDiscount: { label: 'ASN Disc %', maxWidth: 130 },
  asnTotalPrice: { label: 'ASN Total Price', maxWidth: 150 },
  // PO columns (read-only)
  poUnitPrice: { label: 'PO Unit Price', maxWidth: 130 },
  poDiscount: { label: 'PO Disc %', maxWidth: 130 },
  Tax: { label: 'Tax %', maxWidth: 130 },
  poTotalPrice: { label: 'PO Total Price', maxWidth: 150 },
  // ASN columns (editable)

  // Status column
  priceStatus: { label: 'Status', maxWidth: 100 },
  holdCode: { label: 'Hold Code', maxWidth: 120 },
  lpn: { label: 'LPN', maxWidth: 120 },
  received: { label: 'Received', maxWidth: 120 },
  over: { label: 'Over', maxWidth: 100 },
  shortage: { label: 'Shortage', maxWidth: 120 },
  rejected: { label: 'Rejected', maxWidth: 120 },
  cubes: { label: 'Cubes', maxWidth: 100 },
  gross: { label: 'Gross', maxWidth: 100 },
  net: { label: 'Net', maxWidth: 100 },
  dateTime: { label: 'Date/Time', maxWidth: 180 }
};

// Base and extra columns - Removed priceStatus from base columns
const BASE_COLUMNS = [
  'poNumber',
  'city',
  'state',
  'SKUID',
  'description',
  'uom',
  'itemWeight',
  'itemCube',
  'expectedQty',
  'itemPOTolerance',
  'openQty',
  'shortQty',
  'excessQty',
  'damagedQty',
  'dispatchQty',
  'receivedQty',
  'asnUnitPrice',
  'asnDiscount',
  'asnTotalPrice',
  'poUnitPrice',
  'Tax',
  'poDiscount',
  'poTotalPrice'
  // ❌ Removed 'priceStatus' from base columns
];
const EXTRA_COLUMNS = ['packKey', 'lpnNo', 'batchNum', 'manufacturingDate', 'expirationDate', 'holdCode', 'received', 'over', 'shortage', 'rejected', 'cubes', 'gross', 'net', 'dateTime'];

// Filter placeholders
const FILTER_PLACEHOLDERS = {
  expectedQty: 'Exp',
  dispatchQty: 'Disp',
  receivedQty: 'Recv',
  openQty: 'Open qty',
  poNumber: 'PO',
  city: 'City',
  state: 'State',
  dateTime: 'Date',
  SKUID: 'SKU',
  description: 'Desc',
  packKey: 'Pack Key',
  lpnNo: 'LPN No',
  batchNum: 'Batch Num',
  manufacturingDate: 'Mfg Date',
  expirationDate: 'Exp Date',
  holdCode: 'Hold',
  poUnitPrice: 'PO Price',
  asnUnitPrice: 'ASN Price',
  poDiscount: 'PO Disc',
  asnDiscount: 'ASN Disc',
  poTotalPrice: 'PO Total',
  asnTotalPrice: 'ASN Total',
  // itemWeight: 'Weight',
  // itemCube: 'Volume'
};

const SKUSearchResults = ({
  searchResults,
  onDispatchQtyChange,
  onRemoveSku,
  onSelectionChange,
  readOnly = false,
  showGRExtraColumns = false,
  showPriceStatus = false,// ✅ New prop to control price status visibility
  isAddDetailsMode = false,
  hideQtyColumns = false,
  onCaptureSerials = null,
}) => {
  const onCaptureSerialsRef = React.useRef(onCaptureSerials);
  onCaptureSerialsRef.current = onCaptureSerials;
  const canCaptureSerials = !!onCaptureSerials;
  const hasSerialRows = React.useMemo(
    () =>
      Object.entries(searchResults || {}).some(
        ([k, rows]) => !k.startsWith('__') && Array.isArray(rows) && rows.some(r => r?.serialConfig?.inboundEnabled)
      ),
    [searchResults]
  );

  // Column configuration - Conditionally add priceStatus
  const allColumns = React.useMemo(() => {
    
    let columns = [...BASE_COLUMNS];

    if (canCaptureSerials && hasSerialRows) {
      columns.splice(columns.indexOf('dispatchQty') + 1, 0, 'serialCapture');
    }

        // ✅ When requested, hide Short/Excess/Damage/Received qty columns
          if (hideQtyColumns) {
              const HIDDEN_COLS = ['shortQty', 'excessQty', 'damagedQty', 'receivedQty'];
              columns = columns.filter(col => !HIDDEN_COLS.includes(col));
            }

    // ✅ Only add priceStatus column when explicitly requested (view/edit mode)
    if (showPriceStatus) {
      columns.push('priceStatus');
    }

    if (showGRExtraColumns) {
      columns.push(...EXTRA_COLUMNS);
    }

    return columns;
  }, [showGRExtraColumns, showPriceStatus, hideQtyColumns, canCaptureSerials, hasSerialRows]);

  // Header label formatter
  const headerLabelFormatter = React.useCallback((groupKey) => {

    console.log("headerLabelFormatter called with groupKey:", groupKey);

    const groupRows = Array.isArray(searchResults?.[groupKey]) ? searchResults[groupKey] : [];
    const first = groupRows[0] || {};

    if (groupKey.startsWith('SKU::') || groupKey.startsWith('HR')) {
      return `SKU ID: ${first.skuId || groupKey.replace('SKU::', '')}`;
    }
    if (groupKey.startsWith('PO::') || groupKey.startsWith('PO')) {
      return `PO Number: ${first.purchaseOrder || groupKey.replace('PO::', '')}`;
    }
    if (groupKey.startsWith('EPO::')) {
      return `External PO: ${first.externalPoNumber || groupKey.replace('EPO::', '')}`;
    }
    if (groupKey === 'BLIND') {
      return 'Blind Items';
    }
    return groupKey;
  }, [searchResults]);


  // Deterministic header formatter: PO if starts with "PO" or numeric only, otherwise SKU
  const headerLabelFormatterOLD = React.useCallback((groupKey) => {
    console.log("headerLabelFormatter called with groupKey:", groupKey);
    try {
      // normalize
      const rawKey = (typeof groupKey === 'string' ? groupKey.trim() : String(groupKey ?? ''));

      // quick guard: explicit prefixed keys
      console.log("headerLabelFormatter called with rawkey:", groupKey);
      if (rawKey.startsWith('SKU::')) {
        const id = rawKey.replace(/^SKU::/, '').trim();
        return `SKU ID: ${id || 'Unknown'}`;
      }
      if (rawKey.startsWith('PO')) {
        const id = rawKey.replace(/^PO::/, '').trim();
        console.log("headerLabelFormatter PO id:", id);
        return `PO Number: ${id || 'Unknown'}`;
      }

      // get first row if available
      const groupRows = Array.isArray(searchResults?.[groupKey]) ? searchResults[groupKey] : [];
      const first = groupRows[0] || {};

      // prefer explicit createUsing hint if present on the enriched row
      const createUsing = Number(first._createUsing ?? first.createUsing ?? 0);
      if (createUsing === 1) {
        return `PO Number: ${first.purchaseOrder || rawKey}`;
      }
      if (createUsing === 2) {
        return `SKU ID: ${first.skuId || rawKey}`;
      }

      // Deterministic rules:
      // 1) If rawKey starts with "PO" (case-insensitive) => PO
      // 2) If rawKey is numeric-only => PO
      // 3) Otherwise => SKU
      if (/^PO/i.test(rawKey) || /^[0-9]+$/.test(rawKey)) {
        return `PO Number: ${first.purchaseOrder || rawKey}`;
      }

      // fallback: treat as SKU
      return `SKU ID: ${first.skuId || rawKey}`;
    } catch (err) {
      console.error('headerLabelFormatter error:', err);
      return String(groupKey ?? 'Group');
    }
  }, [searchResults]);



  // Helper function to calculate PO Total Price
  const calculatePOTotalPrice = (dispatchQty, unitPrice, discount, tax) => {
    const qty = parseFloat(dispatchQty) || 0;
    const price = parseFloat(unitPrice) || 0;
    const disc = parseFloat(discount) || 0;
    const taxRate = parseFloat(tax) || 0;

    // ✅ Calculate: (qty * price * (1 + tax/100)) * (1 - discount/100)
    const subtotal = qty * price;
    const taxedSubtotal = subtotal * (1 + taxRate / 100);
    const totalPrice = taxedSubtotal * (1 - disc / 100);

    return totalPrice;
  };

  // ✅ Helper function to calculate ASN Total Price - SAME FORMULA AS PO
  const calculateASNTotalPrice = (dispatchQty, asnUnitPrice, asnDiscount, tax) => {
    const qty = parseFloat(dispatchQty) || 0;
    const price = parseFloat(asnUnitPrice) || 0;
    const disc = parseFloat(asnDiscount) || 0;
    const taxRate = parseFloat(tax) || 0;

    // ✅ Calculate: (qty * price * (1 + tax/100)) * (1 - discount/100)
    const subtotal = qty * price;
    const taxedSubtotal = subtotal * (1 + taxRate / 100);
    const totalPrice = taxedSubtotal * (1 - disc / 100);

    return totalPrice;
  };

  // Helper function to get status
  const getPriceStatus = (poTotalPrice, asnTotalPrice) => {
    const poTotal = parseFloat(poTotalPrice) || 0;
    const asnTotal = parseFloat(asnTotalPrice) || 0;

    // Allow small floating point differences (0.01)
    const diff = Math.abs(poTotal - asnTotal);

    if (diff < 0.01) {
      return { icon: '✅', status: 1, label: 'Matched' };
    } else {
      return { icon: '⚠️', status: 2, label: 'Unmatched' };
    }
  };

  // Cell renderer for custom cell content
  const cellRenderer = React.useCallback((column, row, originalIndex, options) => {
    const { readOnly, filterInputStyle, onCellChange, onInputFocus, onInputBlur } = options;

    const expected = row.expectedQty ?? 0;
    const received = row.dispatchQty ?? 0;

    // Extract PO prices from API data
    const poUnitPrice = parseFloat(row.unitPrice) || 0;
    const poDiscount = parseFloat(row.discount) || 0;
    const Tax = parseFloat(row.tax) || 0;

    // ✅ ASN prices (editable) - Use empty string if no value
    const asnUnitPrice = row.asnUnitPrice != null ? parseFloat(row.asnUnitPrice) : null;
    const asnDiscount = row.asnDiscount != null ? parseFloat(row.asnDiscount) : null;

    // ✅ Calculate totals - use fallback to PO values only for calculation
    const calcAsnUnitPrice = asnUnitPrice;
    const calcAsnDiscount = asnDiscount;

    const poTotalPrice = calculatePOTotalPrice(received, poUnitPrice, poDiscount, Tax);
    const asnTotalPrice = calculateASNTotalPrice(received, calcAsnUnitPrice, calcAsnDiscount, Tax);

    // Get status
    const priceStatus = getPriceStatus(poTotalPrice, asnTotalPrice);

    // Extract city and state from nested structure or directly from row
    const city = row.poDetails?.[0]?.buyer?.address?.city || row.city || '-';
    const state = row.poDetails?.[0]?.buyer?.address?.state || row.state || '-';

    // Extract SKU ID from item or skuId field
    const skuId = row.item ?? row.skuId ?? '';

    // Extract PO Number
    const poNumber = row.purchaseOrder || row.poNumber || '-';
    const dispatchedQty = row.asnDispatchQty ?? 0;
    const orderQtyVal = Number(row.expectedQty ?? row.orderedQty ?? row.orderQty) || 0;
    const tolerancePercent = Number(
      row.tolerance ??
      row.itemPOTolerance ??
      row.tolerancePercent ??
      row.tolerence ?? // be permissive with naming
      0
    ) || 0;

    let maxAllowedReceived = Math.max(
      orderQtyVal,
      Number((orderQtyVal + (orderQtyVal * tolerancePercent) / 100).toFixed(6))
    );
    const maxAllowedDispatch = maxAllowedReceived - dispatchedQty;

    // New shared flag used for dispatch + ASN price/discount read-only state
    const disableASNEdit = Boolean(readOnly || isAddDetailsMode);


    const cellData = {
      poNumber: poNumber,
      city: city,
      state: state,
      SKUID: skuId,
      description: row.description ?? `Item ${skuId}`,
      uom: row.uom ?? 'EA',
      itemWeight: row.itemWeight ?? row.weight ?? 0,
      itemCube: row.itemCube ?? row.cube ?? 0,
      expectedQty: expected,
      itemPOTolerance: row.itemPOTolerance ?? 0,
      openQty: row.openQty,
      shortQty: row.shortQty ?? 0,
      excessQty: row.excessQty ?? 0,
      damagedQty: row.damagedQty ?? 0,
      dispatchQty: (
        <input
          type="number"
          className="form-control form-control-sm"
          style={filterInputStyle}
          value={row.dispatchQty === 0 || row.dispatchQty == null ? '' : row.dispatchQty}
          onChange={(e) => {
            if (disableASNEdit) {
              toast.warn("Dispatch quantity cannot be edited in Add Details mode", { autoClose: 2500 });
              return;
            }
            let inputValue = e.target.value;
            let numericValue = inputValue === '' ? 0 : parseFloat(inputValue) || 0;

            // enforce tolerance limit (existing logic)
            if (numericValue > maxAllowedDispatch) {
              numericValue = maxAllowedDispatch;
              toast.warn(`Total Dispatch Qty exceeds PO tolerance. Max allowed : ${maxAllowedDispatch}`, { autoClose: 3000 });
            }

            onCellChange('dispatchQty', numericValue);
          }}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          disabled={disableASNEdit}
          placeholder="Enter qty"
          min="0"
          step="1"
        />
      ),

      serialCapture: row.serialConfig?.inboundEnabled ? (
        <SerialCaptureButton
          className="btn-sm"
          qty={row.dispatchQty}
          captured={countCapturedSerials(
            row.serialDetails,
            row.dispatchQty,
            resolveSerialColumns(row.serialConfig.inboundLabels, row.serialDetails)
          )}
          onClick={() => onCaptureSerialsRef.current?.(options.groupKey, originalIndex)}
        />
      ) : '-',

      receivedQty: (
        row.receivedQty === 0 || row.receivedQty == null ? 0 : row.receivedQty
      ),
      // PO columns (read-only)
      poUnitPrice: poUnitPrice.toFixed(2),
      poDiscount: poDiscount.toFixed(2),
      Tax: Tax.toFixed(2),
      poTotalPrice: poTotalPrice.toFixed(2),

      // ASN columns (editable)
      asnUnitPrice: disableASNEdit ? (
        (calcAsnUnitPrice || 0).toFixed(2)
      ) : (
        <input
          type="number"
          className="form-control form-control-sm"
          style={filterInputStyle}
          value={row.asnUnitPrice === 0 || row.asnUnitPrice == null ? '' : row.asnUnitPrice}
          onChange={(e) => {
            if (disableASNEdit) return;
            const inputValue = e.target.value;
            const numericValue = inputValue === '' ? 0 : parseFloat(inputValue) || 0;
            onCellChange('asnUnitPrice', numericValue);
          }}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          disabled={disableASNEdit}
          placeholder="0.00"
          min="0"
          step="0.01"
        />
      ),

      asnDiscount: disableASNEdit ? (
        (calcAsnDiscount || 0).toFixed(2)
      ) : (
        <input
          type="number"
          className="form-control form-control-sm"
          style={filterInputStyle}
          value={row.asnDiscount === 0 || row.asnDiscount == null ? '' : row.asnDiscount}
          onChange={(e) => {
            if (disableASNEdit) return;
            const inputValue = e.target.value;
            const numericValue = inputValue === '' ? 0 : parseFloat(inputValue) || 0;
            onCellChange('asnDiscount', numericValue);
          }}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          disabled={disableASNEdit}
          placeholder="0.00"
          min="0"
          max="100"
          step="0.01"
        />
      ),

      // ✅ ASN Tax is read-only (inherits from PO Tax)
      asnTotalPrice: (
        <span style={{ fontWeight: 'bold' }}>
          {asnTotalPrice.toFixed(2)}
        </span>
      ),
      // Status column
      priceStatus: (
        <span
          style={{
            fontSize: '1.2em',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px'
          }}
          title={priceStatus.label}
        >
          {priceStatus.icon}
        </span>
      ),
      holdCode: row.holdCode || '-',
      lpnNo: (
        <input
          type="text"
          className="form-control form-control-sm"
          style={filterInputStyle}
          value={row.lpnNo || row.lpn || ''}
          onChange={(e) => onCellChange('lpnNo', e.target.value)}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          disabled={disableASNEdit}
        />
      ),
      batchNum: (
        <input
          type="text"
          className="form-control form-control-sm"
          style={filterInputStyle}
          value={row.batchNum || row.batchNo || row.batchNumber || ''}
          onChange={(e) => onCellChange('batchNum', e.target.value)}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          disabled={disableASNEdit}
        />
      ),
      received,
      over: Math.max(received - expected, 0),
      shortage: Math.max(expected - received, 0),
      rejected: row.rejected ?? 0,
      cubes: row.cubes ?? row.cube ?? '-',
      gross: row.gross ?? row.grossWeight ?? '-',
      net: row.net ?? row.netWeight ?? '-',
      dateTime: row.dateTime || '-',
      packKey: <span>STD</span>,
      manufacturingDate: (
        <input
          type="date"
          className="form-control form-control-sm"
          style={filterInputStyle}
          value={String(row.manufacturingDate || '').slice(0, 10)}
          onChange={(e) => onCellChange('manufacturingDate', e.target.value)}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          disabled={disableASNEdit}
        />
      ),
      expirationDate: (
        <input
          type="date"
          className="form-control form-control-sm"
          style={filterInputStyle}
          value={String(row.expirationDate || '').slice(0, 10)}
          onChange={(e) => onCellChange('expirationDate', e.target.value)}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          disabled={disableASNEdit}
        />
      )
    };

    return cellData[column];
  }, []);

  // Filter function - Updated to include ASN tax
  const filterFunction = React.useCallback((row, originalIndex, filters) => {
    const expected = row.expectedQty ?? 0;
    const received = row.dispatchQty ?? 0;

    // Extract PO prices
    const poUnitPrice = parseFloat(row.unitPrice) || 0;
    const poDiscount = parseFloat(row.discount) || 0;
    const Tax = parseFloat(row.tax) || 0;

    // ✅ ASN prices - INCLUDING TAX
    const asnUnitPrice = parseFloat(row.asnUnitPrice ?? row.unitPrice) || 0;
    const asnDiscount = parseFloat(row.asnDiscount ?? row.discount) || 0;
    // const Tax = parseFloat(row.tax ?? row.tax) || 0;

    // ✅ Calculate totals using dispatchQty and including tax
    const poTotalPrice = calculatePOTotalPrice(received, poUnitPrice, poDiscount, Tax);
    const asnTotalPrice = calculateASNTotalPrice(received, asnUnitPrice, asnDiscount, Tax);

    // Extract city and state from nested structure or directly from row
    const city = row.poDetails?.[0]?.buyer?.address?.city || row.city || '';
    const state = row.poDetails?.[0]?.buyer?.address?.state || row.state || '';

    // Extract SKU ID from item or skuId field
    const skuId = row.item ?? row.skuId ?? '';

    // Extract PO Number
    const poNumber = row.purchaseOrder || row.poNumber || '';

    // Create a derived row with all filterable fields properly mapped
    const derivedRow = {
      ...row,
      poNumber: poNumber,
      city: city,
      state: state,
      SKUID: skuId,
      description: row.description || '',
      uom: row.uom || '',
      expectedQty: expected,
      openQty: expected,
      dispatchQty: received,
      itemPOTolerance: 10,
      poUnitPrice: poUnitPrice,
      poDiscount: poDiscount,
      Tax: Tax,
      poTotalPrice: poTotalPrice,
      asnUnitPrice: asnUnitPrice,
      asnDiscount: asnDiscount,
      // Tax: Tax,
      asnTotalPrice: asnTotalPrice,
      received: received,
      over: Math.max(received - expected, 0),
      shortage: Math.max(expected - received, 0),
      rejected: row.rejected ?? 0,
      cubes: row.cubes ?? row.cube ?? '',
      gross: row.gross ?? row.grossWeight ?? '',
      net: row.net ?? row.netWeight ?? '',
      dateTime: row.dateTime || '',
      holdCode: row.holdCode || '',
      lpnNo: row.lpnNo || row.lpn || '',
      batchNum: row.batchNum || row.batchNo || row.batchNumber || '',
      packKey: row.packKey || '',
      manufacturingDate: row.manufacturingDate || '',
      expirationDate: row.expirationDate || ''
    };

    // Apply filters to all fields
    return Object.entries(filters).every(([key, filterVal]) => {
      if (!filterVal) return true;

      const val = derivedRow[key];

      // Convert both to string for comparison, handle null/undefined
      const stringVal = String(val ?? '').toLowerCase();
      const stringFilter = String(filterVal ?? '').toLowerCase();

      return stringVal.includes(stringFilter);
    });
  }, []);

  // Handle cell changes - Added ASN price handlers
  const handleCellChangeOLD = React.useCallback((groupKey, originalIndex, column, value) => {
    if ((column === 'dispatchQty' || column === 'asnUnitPrice' || column === 'asnDiscount') && onDispatchQtyChange) {
      // Pass column name as 4th parameter to distinguish field type
      onDispatchQtyChange(groupKey, originalIndex, value, column);
    }
  }, [onDispatchQtyChange]);


  const handleCellChange = React.useCallback((groupKey, originalIndex, column, value) => {
    // block dispatch edits when in Add Details mode
    if (column === 'dispatchQty' && isAddDetailsMode) {
      toast.warn("Cannot edit dispatch qty while ASN is in Add Details mode", { autoClose: 2500 });
      return;
    }

    if ((column === 'dispatchQty' || column === 'asnUnitPrice' || column === 'asnDiscount' || column === 'manufacturingDate' || column === 'expirationDate' || column === 'lpnNo' || column === 'batchNum') && onDispatchQtyChange) {
      onDispatchQtyChange(groupKey, originalIndex, value, column);
    }
  }, [onDispatchQtyChange, isAddDetailsMode]);


  return (
    <ItemsTable
      data={searchResults}
      columns={allColumns}
      columnConfigs={COLUMN_CONFIGS}
      filterPlaceholders={FILTER_PLACEHOLDERS}
      cellRenderer={cellRenderer}
      headerLabelFormatter={headerLabelFormatter}
      filterFunction={filterFunction}
      onCellChange={handleCellChange}
      onRemoveGroup={onRemoveSku}
      onSelectionChange={onSelectionChange}
      readOnly={readOnly}
      autoSelectOnInput={true}
    />
  );
};

export default React.memo(SKUSearchResults);
